import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Türkiye cep telefonu için maskeli input. Görünüm `0 (555) 555 55 55`,
 * DB'ye 11 haneli ham `05555555555` olarak gider.
 *
 * `0 (5` sabit prefix daima görünür; kullanıcı sadece kalan 9 haneyi girer.
 * Parantez `)` 2 hane girildikten sonra eklenir, gruplar 3-2-2 boşlukla
 * ayrılır.
 *
 * Form contract:
 *  - Boş input → onChange("") çağrılır.
 *  - Geçerli kısmi/tam giriş → onChange("05" + 1..9 hane).
 */
export interface PhoneInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange"
  > {
  value: string;
  onChange: (next: string) => void;
}

const PREFIX = "0 (5";

function formatPhone(digits: string): string {
  // digits: string of 0-9 chars, max 9 (kullanıcı girdisi, "05" prefix hariç)
  let out = PREFIX;
  if (digits.length === 0) return out;
  out += digits[0];
  if (digits.length === 1) return out;
  out += digits[1] + ")";
  if (digits.length === 2) return out;
  out += " " + digits.slice(2, 5);
  if (digits.length <= 5) return out;
  out += " " + digits.slice(5, 7);
  if (digits.length <= 7) return out;
  out += " " + digits.slice(7, 9);
  return out;
}

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, className, ...rest }, ref) => {
    const inner = value.startsWith("05") ? value.slice(2, 11) : "";
    const display = formatPhone(inner);

    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const setRefs = (el: HTMLInputElement | null) => {
      innerRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref)
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
    };

    // Maskeyi düzenleyebilmek için onChange'de tüm haneleri çıkar, "05"
    // prefix'ini at, kalanı kullanıcı girişi say.
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const all = e.target.value.replace(/\D/g, "");
      let user: string;
      if (all.startsWith("05")) user = all.slice(2);
      else if (all.startsWith("0")) user = all.slice(1).replace(/^5/, "");
      else if (all.startsWith("5")) user = all.slice(1);
      else user = all;
      user = user.slice(0, 9);
      onChange(user.length > 0 ? "05" + user : "");
    };

    // Maske güncellendikten sonra (display değişince) cursor'ı sona getir,
    // yani kullanıcı her zaman en sondaki yere yazıyor olsun.
    React.useEffect(() => {
      const el = innerRef.current;
      if (el && document.activeElement === el) {
        const end = el.value.length;
        // Microtask ile setSelectionRange, React render'ı sonrası
        queueMicrotask(() => el.setSelectionRange(end, end));
      }
    }, [display]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      const target = e.currentTarget;
      // Boşken cursor'ı `0 (5` sonuna konumlandır
      queueMicrotask(() => {
        const end = target.value.length;
        target.setSelectionRange(end, end);
      });
    };

    // Backspace/Delete'in sabit prefix veya formatlama karakterlerinde
    // (parantez, boşluk) tıkanmasını önle, her durumda son haneyi sil.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        if (inner.length > 0) {
          const next = inner.slice(0, -1);
          onChange(next.length > 0 ? "05" + next : "");
        }
      }
    };

    return (
      <input
        ref={setRefs}
        type="tel"
        inputMode="numeric"
        value={display}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...rest}
      />
    );
  },
);
PhoneInput.displayName = "PhoneInput";
